// DCI (Legionella Dashboard) specific flow: This file will be removed once API for DCI (Legionella Dashboard) is unified in BED.
import { ChatContent } from '../models/chat.model';
import { EvaDciQueryParams, EvaDciResponse } from '../models/eva-request.model';

const EVA_LEGIONELLA_CONTEXT_STORAGE_KEY = 'eva.legionella.context';
const LEGIONELLA_PARSE_ERROR_MESSAGE = 'Sorry, I could not parse the response. Please try again.';

export function getLegionellaEvaDciContext(): EvaDciQueryParams {
  const defaultContext = getDefaultLegionellaEvaDciContext();

  if (typeof window === 'undefined') {
    return defaultContext;
  }

  try {
    const value = sessionStorage.getItem(EVA_LEGIONELLA_CONTEXT_STORAGE_KEY);
    if (!value) {
      return defaultContext;
    }

    const parsed = JSON.parse(value);
    const siteIds = Array.isArray(parsed?.siteIds)
      ? parsed.siteIds
        .map((siteId: unknown) => Number(siteId))
        .filter((siteId: number) => !Number.isNaN(siteId))
      : [];
    const corporateCpId = Number(parsed?.corporateCpId ?? 0);

    return {
      siteIds,
      startDate: typeof parsed?.startDate === 'string' && parsed.startDate.length ? parsed.startDate : defaultContext.startDate,
      endDate: typeof parsed?.endDate === 'string' && parsed.endDate.length ? parsed.endDate : defaultContext.endDate,
      corporateCpId: Number.isNaN(corporateCpId) ? 0 : corporateCpId
    };
  } catch {
    return defaultContext;
  }
}

export function getLegionellaSessionId(response: EvaDciResponse | null): string | null {
  if (!response) {
    return null;
  }

  const sessionId = response.SessionId;
  return typeof sessionId === 'string' && sessionId.trim().length ? sessionId : null;
}

export function getLegionellaResponseContent(response: EvaDciResponse | null): ChatContent {
  const responseText = getLegionellaResponseText(response);

  if (containsLegionellaMermaid(responseText)) {
    return {
      type: 'text',
      data: LEGIONELLA_PARSE_ERROR_MESSAGE
    };
  }

  if (!containsLegionellaTable(responseText)) {
    return {
      type: 'text',
      data: responseText
    };
  }

  return parseLegionellaTableContent(responseText);
}

function getDefaultLegionellaEvaDciContext(): EvaDciQueryParams {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 365);

  return {
    siteIds: [],
    startDate: formatDateValue(startDate),
    endDate: formatDateValue(endDate),
    corporateCpId: 0
  };
}

function formatDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getLegionellaResponseText(response: EvaDciResponse | null): string {
  if (!response) {
    return LEGIONELLA_PARSE_ERROR_MESSAGE;
  }

  const responseText = response.Message;

  if (typeof responseText === 'string' && responseText.trim().length) {
    return responseText;
  }

  return LEGIONELLA_PARSE_ERROR_MESSAGE;
}

function containsLegionellaMermaid(message: string): boolean {
  return /```mermaid[\s\S]*?```/i.test(message);
}

function containsLegionellaTable(message: string): boolean {
  return /\|.*\|\s*\n\|\s*[-:]+/i.test(message);
}

// Splits a markdown response into the first table and the surrounding text blocks.
// Anything after the first table (including additional tables) is preserved verbatim
// in footerText so it stays visible to the user.
function parseLegionellaTableContent(message: string): ChatContent {
  const normalized = (message || '').replace(/\r\n/g, '\n').trim();
  const lines = normalized.split('\n');

  const beforeLines: string[] = [];
  const tableLines: string[] = [];
  const afterLines: string[] = [];

  let phase: 'before' | 'in-table' | 'after' = 'before';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (phase === 'before') {
      if (isMarkdownTableStart(lines, i)) {
        phase = 'in-table';
        tableLines.push(line);
      } else {
        beforeLines.push(line);
      }
      continue;
    }

    if (phase === 'in-table') {
      if (looksLikeMarkdownTableLine(line)) {
        tableLines.push(line);
      } else {
        phase = 'after';
        afterLines.push(line);
      }
      continue;
    }

    afterLines.push(line);
  }

  const rows = tableLines
    .map(line => parseMarkdownTableRow(line))
    .filter(row => row.length > 0);

  // rows[0] is the header, rows[1] is the separator (---), rows[2+] are body rows.
  if (rows.length < 2) {
    return {
      type: 'text',
      data: normalized
    };
  }

  const headers = rows[0];
  const bodyRows = rows.slice(2).map(row => {
    const padded = [...row];
    while (padded.length < headers.length) {
      padded.push('');
    }
    return padded.slice(0, headers.length);
  });

  const description = beforeLines.join('\n').trim();
  const footerText = afterLines.join('\n').trim();

  return {
    type: 'table',
    data: { headers, rows: bodyRows },
    metadata: {
      ...(description ? { description } : {}),
      ...(footerText ? { footerText } : {})
    }
  };
}

function isMarkdownTableStart(lines: string[], index: number): boolean {
  if (index + 1 >= lines.length) {
    return false;
  }

  const header = lines[index];
  const separator = lines[index + 1];

  return looksLikeMarkdownTableLine(header) && isMarkdownTableSeparator(separator);
}

function looksLikeMarkdownTableLine(line: string): boolean {
  if (!line) {
    return false;
  }

  return line.trim().startsWith('|') && line.trim().endsWith('|');
}

function isMarkdownTableSeparator(line: string): boolean {
  if (!line) {
    return false;
  }

  const separatorPattern = /^\|\s*[:\-]+\s*(\|\s*[:\-]+\s*)+\|\s*$/;
  return separatorPattern.test(line.trim());
}

function parseMarkdownTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());
}
